/**
 * Edit-state types and small helpers used across the cadence step
 * components. The legacy add/edit JSX components that previously lived
 * here were retired when CampaignDayCardPure took over rendering and
 * editing — only the shared discriminated-union types, `daysToMs` and the
 * editor → mutation payload mappers remain.
 */
import type { SiftConfig, StepData, StepType } from './CTSContentEditingPure';
import type { StepUpdateInputPure } from './types';

export type CadenceStepOption = 'one' | 'two';

/** Edit state for adding a new step */
export type EditAddStepPure = {
  type: 'add';
  afterStepId?: string;
  option: CadenceStepOption;
  /**
   * Disambiguates "add to this day's group" (`'in-group'`, default) from
   * "add a brand-new day before the next card" (`'new-day'`). Both share
   * the same `afterStepId` so the discriminator avoids double-rendering
   * the picker (once in the previous day's card and once in the next day's
   * leading wait band).
   */
  intent?: 'in-group' | 'new-day';
  data?: {
    id: string;
    type: StepType;
    delay: number;
    option?: CadenceStepOption;
    subject?: string | null;
    body2?: string | null;
    postsToLike?: number | null;
    cc?: string | null;
    bcc?: string | null;
    onlyOpenLink?: boolean;
    siftConditions?: SiftConfig | null;
  };
};

/** Edit state for editing delay */
export type EditDelayStepPure = { type: 'delay'; stepId: string };

/** Edit state for editing content */
export type EditContentStepPure = { type: 'edit'; option: CadenceStepOption; stepId: string };

/** Combined edit state */
export type EditStepPure = EditDelayStepPure | EditContentStepPure | EditAddStepPure;

/** Data required to create a new step */
export type CreateStepData = {
  type: StepType;
  delay: number;
  option?: CadenceStepOption;
  afterStepId?: string;
  subject?: string;
  body2?: string;
  postsToLike?: number;
  cc?: string;
  bcc?: string;
  onlyOpenLink?: boolean;
  siftConditions?: SiftConfig;
  /** Email only: continue the previous email's thread. Absent ⇒ DB default (true). */
  replyToPrevious?: boolean;
};

/** Convert days to milliseconds */
export function daysToMs(days: number): number {
  return days * 24 * 60 * 60 * 1000;
}

/**
 * The inline editor's submit → a sparse create payload for a step at `placement`. Only fields the
 * editor actually populated are included: the create mutation's Zod schema rejects `null`.
 */
export function createStepDataFromEditor(
  type: StepType,
  data: StepData,
  placement: Pick<CreateStepData, 'delay' | 'afterStepId' | 'option'>,
): CreateStepData {
  const create: CreateStepData = { type, ...placement };
  if (typeof data.subject === 'string') create.subject = data.subject;
  if (typeof data.body === 'string') create.body2 = data.body;
  if (typeof data.postsToLike === 'number') create.postsToLike = data.postsToLike;
  if (typeof data.cc === 'string') create.cc = data.cc;
  if (typeof data.bcc === 'string') create.bcc = data.bcc;
  if (data.siftConditions != null) create.siftConditions = data.siftConditions;
  // InMail's "only open link" flag defaults to `false` so the created step has a deterministic
  // value rather than relying on server-side / db column defaults.
  if (type === 'inmail') create.onlyOpenLink = data.onlyOpenLink ?? false;
  if (typeof data.replyToPrevious === 'boolean') create.replyToPrevious = data.replyToPrevious;
  return create;
}

/**
 * The inline editor's submit → a sparse update payload. The update mutation's schema accepts
 * `string | undefined` but rejects null, so null / absent fields are omitted ("no change").
 */
export function updateStepDataFromEditor(data: StepData): StepUpdateInputPure {
  const update: StepUpdateInputPure = {};
  if (typeof data.subject === 'string') update.subject = data.subject;
  if (typeof data.body === 'string') update.body2 = data.body;
  if (typeof data.postsToLike === 'number') update.postsToLike = data.postsToLike;
  if (typeof data.cc === 'string') update.cc = data.cc;
  if (typeof data.bcc === 'string') update.bcc = data.bcc;
  if (data.siftConditions != null) update.siftConditions = data.siftConditions;
  if (typeof data.onlyOpenLink === 'boolean') update.onlyOpenLink = data.onlyOpenLink;
  if (typeof data.replyToPrevious === 'boolean') update.replyToPrevious = data.replyToPrevious;
  return update;
}
